import type { ExerciseTemplate, Trap } from '@yuan-math/exercises';
import { roundTo } from '@yuan-math/math';
import {
  choiceExercise,
  decimalTolerance,
  numericExercise,
  options,
  template,
  trap,
} from './authoring.ts';

/**
 * Inference: sampling distributions, intervals, tests, correlation and
 * regression.
 *
 * Almost every item in this file is arithmetic on one quantity — σ/√n — and
 * almost every wrong answer is that quantity computed as something else: σ
 * itself, or σ/n, or σ with the √n applied to the wrong end. So the traps here
 * are unusually uniform, and unusually valuable: a student who reaches for σ
 * where the standard error belongs will do it in a confidence interval, in a
 * hypothesis test and in a probability about a mean, and one named diagnosis
 * covers all three.
 *
 * Three constructions are deliberate:
 *
 * **`sample-sizes` are perfect squares** wherever a standard error is computed
 * by hand, so σ/√n is exact and the trap sitting at σ/n is unambiguously a
 * different number rather than a rounding away.
 *
 * **The interval items carry their critical value in the prompt's confidence
 * level, not in the numbers**, because choosing z* is half the skill — and
 * `critical-value-level-mismatched` is only reachable if the level actually
 * varies between items.
 *
 * **The regression items are given summary statistics rather than raw points.**
 * b = r(s_y/s_x) is the sentence the concept exists to make sayable, and a
 * question handing over eight coordinate pairs tests arithmetic stamina
 * instead.
 */

/** Drops any trap repeating the answer or an earlier trap. */
const distinct = (answer: number | string, traps: Trap[]): Trap[] => {
  const seen = new Set<string>([String(answer)]);
  return traps.filter((entry) => {
    if (seen.has(entry.response)) return false;
    seen.add(entry.response);
    return true;
  });
};

/** Sample sizes with exact square roots, so σ/√n is a clean number. */
const SQUARE_SAMPLES = [16, 25, 36, 49, 64, 81, 100, 144, 225, 400] as const;

/** The three critical values, and the levels that select them. */
const LEVELS = [
  { percent: 90, z: 1.645 },
  { percent: 95, z: 1.96 },
  { percent: 99, z: 2.576 },
] as const;

/**
 * Φ(z), the area to the left under the standard normal.
 *
 * Abramowitz & Stegun 7.1.26 — accurate to about 1.5 × 10⁻⁷, comfortably
 * inside the three decimal places every p-value item asks for.
 */
function phi(z: number): number {
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * x);
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) *
      t *
      Math.exp(-x * x);
  return 0.5 * (1 + sign * y);
}

export const INFERENCE_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------ sampling distributions */
  template({
    id: 'standard-error-of-a-mean',
    conceptId: 'sampling-distributions',
    description: 'Compute the standard error of a sample mean.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const sd = rng.int(3, 30);
      const n = rng.pick(SQUARE_SAMPLES);
      const answer = roundTo(sd / Math.sqrt(n), 3);

      return numericExercise({
        prompt: `A population has standard deviation ${sd}. Samples of ${n} are taken. What is the standard deviation of the sample mean? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `The standard error is σ/√n = ${sd} ÷ ${Math.sqrt(n)} = ${answer}. Averaging ${n} observations shrinks the spread by a factor of √${n}, not by ${n}.`,
        hint: 'A mean of many observations wobbles less than one observation. By what factor?',
        steps: [
          { label: 'The square root of the sample size.', latex: `\\sqrt{${n}} = ${Math.sqrt(n)}` },
          { label: 'Divide the population standard deviation by it.', latex: `\\frac{${sd}}{${Math.sqrt(n)}} = ${answer}` },
        ],
        traps: distinct(answer, [
          // Divided by n rather than by √n.
          trap(roundTo(sd / n, 3), 'standard-error-divided-by-n'),
          // The population standard deviation, unshrunk.
          trap(sd, 'standard-error-not-used'),
        ]),
      });
    },
  }),

  template({
    id: 'probability-about-a-sample-mean',
    conceptId: 'sampling-distributions',
    description: 'A tail probability for a mean, not for one observation.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const mean = rng.pick([20, 50, 100, 200, 500]);
      const sd = rng.pick([3, 4, 6, 8, 10, 12]);
      const n = rng.pick([16, 25, 36, 64, 100]);
      const se = sd / Math.sqrt(n);
      const z = rng.pick([1.2, 1.5, 1.8, 2, 2.4]);
      const cut = roundTo(mean + z * se, 2);
      const answer = roundTo(1 - phi(z), 3);
      const individualZ = (cut - mean) / sd;

      return numericExercise({
        prompt: `A population has mean ${mean} and standard deviation ${sd}. A sample of ${n} is taken. What is the probability that the sample mean exceeds ${cut}? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `The sample mean has standard error ${sd}/√${n} = ${roundTo(se, 3)}, so z = (${cut} − ${mean}) ÷ ${roundTo(se, 3)} = ${z} and the upper tail is ≈ ${answer}.`,
        hint: 'The question is about a mean of ' + n + ' observations, not about one. Which spread applies?',
        steps: [
          { label: 'Standard error of the mean.', latex: `\\frac{${sd}}{\\sqrt{${n}}} = ${roundTo(se, 3)}` },
          { label: 'Standardise with it.', latex: `z = \\frac{${cut} - ${mean}}{${roundTo(se, 3)}} = ${z}` },
          { label: 'Read the upper tail.', latex: `1 - \\Phi(${z}) \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // σ used where the standard error belonged — the answer for one observation.
          trap(roundTo(1 - phi(individualZ), 3), 'standard-error-not-used'),
          // The lower tail.
          trap(roundTo(phi(z), 3), 'normal-area-complemented'),
          // σ/n rather than σ/√n.
          trap(roundTo(1 - phi((cut - mean) / (sd / n)), 3), 'standard-error-divided-by-n'),
        ]),
      });
    },
  }),

  template({
    id: 'central-limit-theorem-claim',
    conceptId: 'sampling-distributions',
    description: 'Say what the central limit theorem is and is not about.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const n = rng.pick([40, 50, 60, 80, 100, 150]);
      const skewed = rng.pick(['household incomes', 'waiting times at a clinic', 'insurance claim sizes', 'house prices']);

      return choiceExercise({
        prompt: `${skewed.charAt(0).toUpperCase()}${skewed.slice(1)} are strongly right-skewed. Samples of ${n} are taken. What does the central limit theorem say?`,
        ...options(
          [
            { label: `The distribution of the sample mean is approximately normal`, correct: true },
            {
              label: `The population becomes approximately normal once the sample is this large`,
              misconceptionId: 'clt-applied-to-population',
            },
            {
              label: `Individual values are now approximately normally distributed`,
              misconceptionId: 'clt-applied-to-population',
            },
            {
              label: `Nothing — the theorem needs a normal population to start with`,
              misconceptionId: 'normal-assumed-for-any-data',
            },
          ],
          rng.shuffle,
        ),
        summary: `The theorem is a claim about *sample means*, and it holds whatever the population looks like. The ${skewed} stay just as skewed as they were; it is the averages of ${n} of them that straighten out into a bell.`,
        hint: 'Which quantity does the theorem describe the distribution of — an observation, or an average?',
      });
    },
  }),

  /* ---------------------------------------------------- confidence intervals */
  template({
    id: 'confidence-interval-margin',
    conceptId: 'confidence-intervals',
    description: 'Compute a margin of error at a stated level.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const sd = rng.int(4, 30);
      const n = rng.pick(SQUARE_SAMPLES);
      const level = rng.pick(LEVELS);
      const se = sd / Math.sqrt(n);
      const answer = roundTo(level.z * se, 3);

      return numericExercise({
        prompt: `A sample of ${n} is drawn from a population with standard deviation ${sd}. What is the margin of error for a ${level.percent}% confidence interval for the mean? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `Standard error ${sd}/√${n} = ${roundTo(se, 4)}, and ${level.percent}% confidence uses z* = ${level.z}: margin = ${level.z} × ${roundTo(se, 4)} ≈ ${answer}.`,
        hint: 'Two things are needed: how much a sample mean wobbles, and how many of those wobbles this confidence level demands.',
        steps: [
          { label: 'Standard error.', latex: `\\frac{${sd}}{\\sqrt{${n}}} = ${roundTo(se, 4)}` },
          { label: `Critical value for ${level.percent}%.`, latex: `z^{*} = ${level.z}` },
          { label: 'Multiply.', latex: `${level.z} \\times ${roundTo(se, 4)} \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // One standard error, with no critical value at all.
          trap(roundTo(se, 3), 'critical-value-omitted'),
          // A different level's critical value.
          trap(roundTo((level.percent === 95 ? 2.576 : 1.96) * se, 3), 'critical-value-level-mismatched'),
          // σ/n rather than σ/√n.
          trap(roundTo(level.z * (sd / n), 3), 'standard-error-divided-by-n'),
        ]),
      });
    },
  }),

  template({
    id: 'confidence-interval-endpoint',
    conceptId: 'confidence-intervals',
    description: 'Find one endpoint of a confidence interval for a mean.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const mean = rng.pick([25, 48, 72, 96, 140, 300]) + rng.int(0, 9) / 10;
      const sd = rng.int(4, 24);
      const n = rng.pick(SQUARE_SAMPLES);
      const level = rng.pick(LEVELS);
      const se = sd / Math.sqrt(n);
      const margin = level.z * se;
      const upper = rng.bool();
      const answer = roundTo(upper ? mean + margin : mean - margin, 3);

      return numericExercise({
        prompt: `A sample of ${n} has mean ${roundTo(mean, 1)}, drawn from a population with standard deviation ${sd}. What is the ${upper ? 'upper' : 'lower'} end of the ${level.percent}% confidence interval for the population mean? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `Margin = ${level.z} × ${sd}/√${n} = ${roundTo(margin, 4)}, so the interval is ${roundTo(mean, 1)} ± ${roundTo(margin, 4)} and its ${upper ? 'upper' : 'lower'} end is ${answer}.`,
        hint: 'Build the margin of error first. The interval is the sample mean plus and minus it.',
        steps: [
          { label: 'Standard error.', latex: `\\frac{${sd}}{\\sqrt{${n}}} = ${roundTo(se, 4)}` },
          { label: 'Margin of error.', latex: `${level.z} \\times ${roundTo(se, 4)} = ${roundTo(margin, 4)}` },
          { label: `The ${upper ? 'upper' : 'lower'} end.`, latex: `${roundTo(mean, 1)} ${upper ? '+' : '-'} ${roundTo(margin, 4)} = ${answer}` },
        ],
        traps: distinct(answer, [
          // The other endpoint.
          trap(roundTo(upper ? mean - margin : mean + margin, 3), 'critical-value-level-mismatched'),
          // The critical value left out.
          trap(roundTo(upper ? mean + se : mean - se, 3), 'critical-value-omitted'),
          // The sample mean reported as though it were the answer.
          trap(roundTo(mean, 3), 'critical-value-omitted'),
        ]),
      });
    },
  }),

  template({
    id: 'required-sample-size',
    conceptId: 'confidence-intervals',
    description: 'Solve a margin-of-error requirement for n, rounding up.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const sd = rng.int(5, 30);
      const level = rng.pick(LEVELS);
      const margin = rng.pick([0.5, 1, 1.5, 2, 2.5, 3]);
      const exact = ((level.z * sd) / margin) ** 2;
      const answer = Math.ceil(exact);

      return numericExercise({
        prompt: `A population has standard deviation ${sd}. How large a sample is needed for a ${level.percent}% confidence interval for the mean to have a margin of error of at most ${margin}?`,
        value: answer,
        summary: `Set ${level.z} × ${sd}/√n ≤ ${margin}, so √n ≥ ${roundTo(Math.sqrt(exact), 3)} and n ≥ ${roundTo(exact, 2)}. A fractional observation is not a sample, so round up: ${answer}.`,
        hint: 'Write the margin-of-error formula as an inequality and make n the subject. Then think about which way to round.',
        steps: [
          { label: 'Set up the requirement.', latex: `${level.z} \\cdot \\frac{${sd}}{\\sqrt{n}} \\le ${margin}` },
          { label: 'Make √n the subject.', latex: `\\sqrt{n} \\ge \\frac{${level.z} \\times ${sd}}{${margin}} = ${roundTo(Math.sqrt(exact), 3)}` },
          { label: 'Square, then round up.', latex: `n \\ge ${roundTo(exact, 2)} \\Rightarrow n = ${answer}` },
        ],
        traps: distinct(answer, [
          // Rounded down, leaving the requirement unmet.
          ...(Math.floor(exact) !== answer ? [trap(Math.floor(exact), 'sample-size-rounded-down')] : []),
          // The square root never taken — n confused with √n.
          trap(Math.ceil(Math.sqrt(exact)), 'standard-error-divided-by-n'),
          // The critical value dropped.
          trap(Math.ceil((sd / margin) ** 2), 'critical-value-omitted'),
        ]),
      });
    },
  }),

  template({
    id: 'confidence-interpretation',
    conceptId: 'confidence-intervals',
    description: 'Interpret a confidence interval without over-claiming.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const low = rng.int(40, 160);
      const width = rng.int(2, 9);
      const high = low + width;
      const level = rng.pick(LEVELS);

      return choiceExercise({
        prompt: `A ${level.percent}% confidence interval for a population mean is (${low}, ${high}). Which statement is correct?`,
        ...options(
          [
            {
              label: `We are ${level.percent}% confident the population mean lies between ${low} and ${high}`,
              correct: true,
            },
            {
              label: `There is a ${level.percent}% probability the population mean lies between ${low} and ${high}`,
              misconceptionId: 'confidence-read-as-probability',
            },
            {
              label: `${level.percent}% of the population lies between ${low} and ${high}`,
              misconceptionId: 'confidence-read-as-probability',
            },
            {
              label: `${level.percent}% of future sample means will lie between ${low} and ${high}`,
              misconceptionId: 'clt-applied-to-population',
            },
          ],
          rng.shuffle,
        ),
        summary: `The population mean is a fixed number: it is either in (${low}, ${high}) or it is not, and no probability attaches to a fact. The ${level.percent}% describes the procedure — intervals built this way capture the mean ${level.percent}% of the time.`,
        hint: 'What exactly is the ' + level.percent + '% a property of — this interval, or the method that produced it?',
      });
    },
  }),

  /* ------------------------------------------------------ hypothesis testing */
  template({
    id: 'test-statistic-z',
    conceptId: 'hypothesis-testing',
    description: 'Compute a test statistic for a sample mean.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const nullMean = rng.pick([50, 100, 250, 500, 1000]);
      const sd = rng.int(4, 24);
      const n = rng.pick(SQUARE_SAMPLES);
      const se = sd / Math.sqrt(n);
      const z = rng.pick([-2.4, -1.8, -1.2, 1.4, 1.9, 2.3]);
      const sampleMean = roundTo(nullMean + z * se, 2);
      const answer = roundTo(z, 3);

      return numericExercise({
        prompt: `A machine should produce a mean of ${nullMean}, with standard deviation ${sd}. A sample of ${n} has mean ${sampleMean}. What is the value of the test statistic z? Give your answer to two decimal places, keeping the sign.`,
        value: roundTo(answer, 2),
        tolerance: decimalTolerance(roundTo(answer, 2), 2),
        summary: `z counts standard errors, not units: z = (${sampleMean} − ${nullMean}) ÷ (${sd}/√${n}) = ${roundTo(answer, 2)}.`,
        hint: 'How far is the sample mean from the null value, measured in standard errors of the mean?',
        steps: [
          { label: 'Standard error.', latex: `\\frac{${sd}}{\\sqrt{${n}}} = ${roundTo(se, 4)}` },
          { label: 'Distance from the null value.', latex: `${sampleMean} - ${nullMean} = ${roundTo(sampleMean - nullMean, 3)}` },
          { label: 'Divide.', latex: `z = ${roundTo(answer, 2)}` },
        ],
        traps: distinct(roundTo(answer, 2), [
          // σ used where the standard error belonged.
          trap(roundTo((sampleMean - nullMean) / sd, 2), 'standard-error-not-used'),
          // σ/n rather than σ/√n.
          trap(roundTo((sampleMean - nullMean) / (sd / n), 2), 'standard-error-divided-by-n'),
          // The raw distance, never standardised.
          trap(roundTo(sampleMean - nullMean, 2), 'z-score-not-divided-by-sd'),
        ]),
      });
    },
  }),

  template({
    id: 'p-value-two-tailed',
    conceptId: 'hypothesis-testing',
    description: 'Turn a test statistic into a p-value, with the right number of tails.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const z = rng.pick([1.15, 1.42, 1.68, 1.93, 2.05, 2.34, 2.61]);
      const twoTailed = rng.bool();
      const negative = rng.bool();
      const statistic = negative ? -z : z;
      const oneTail = 1 - phi(z);
      const answer = roundTo(twoTailed ? 2 * oneTail : oneTail, 3);

      return numericExercise({
        prompt: `A test of H₀: μ = μ₀ against H₁: μ ${twoTailed ? '≠' : negative ? '<' : '>'} μ₀ gives a test statistic of z = ${statistic}. What is the p-value? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `The tail beyond |z| = ${z} has area ${roundTo(oneTail, 4)}. ${twoTailed ? 'The alternative is two-sided, so "at least this extreme" includes the other direction too: double it.' : 'The alternative points one way, so one tail is the whole p-value.'} p ≈ ${answer}.`,
        hint: 'How many directions count as "at least this extreme" for this alternative hypothesis?',
        steps: [
          { label: 'Area beyond the statistic in one tail.', latex: `1 - \\Phi(${z}) \\approx ${roundTo(oneTail, 4)}` },
          {
            label: twoTailed ? 'Two-sided, so both tails count.' : 'One-sided, so one tail is enough.',
            latex: `p \\approx ${answer}`,
          },
        ],
        traps: distinct(answer, [
          // The wrong number of tails.
          trap(roundTo(twoTailed ? oneTail : 2 * oneTail, 3), 'two-tailed-p-not-doubled'),
          // The area on the other side of the statistic.
          trap(roundTo(phi(z), 3), 'normal-area-complemented'),
        ]),
      });
    },
  }),

  template({
    id: 'test-conclusion',
    conceptId: 'hypothesis-testing',
    description: 'Draw the right conclusion from a p-value.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const alpha = rng.pick([0.01, 0.05, 0.1]);
      const reject = rng.bool();
      const p = reject
        ? roundTo(alpha * rng.pick([0.2, 0.4, 0.6]), 4)
        : roundTo(alpha + rng.pick([0.02, 0.05, 0.12]), 4);

      return choiceExercise({
        prompt: `A test of whether a machine is off target gives p = ${p}, at a significance level of α = ${alpha}. What is the conclusion?`,
        ...options(
          [
            reject
              ? { label: 'Reject H₀: there is evidence the machine is off target', correct: true }
              : { label: 'Do not reject H₀: there is no evidence the machine is off target', correct: true },
            reject
              ? { label: 'Reject H₀: this proves the machine is off target', misconceptionId: 'significance-taken-as-importance' }
              : { label: 'Do not reject H₀: this proves the machine is on target', misconceptionId: 'nonsignificant-taken-as-proof' },
            {
              label: `The probability that H₀ is true is ${p}`,
              misconceptionId: 'p-value-as-probability-of-null',
            },
            reject
              ? { label: `Reject H₀, and the machine is badly off target because p is small`, misconceptionId: 'significance-taken-as-importance' }
              : { label: `Reject H₀, because p is small in absolute terms`, misconceptionId: 'nonsignificant-taken-as-proof' },
          ],
          rng.shuffle,
        ),
        summary: reject
          ? `p = ${p} is below α = ${alpha}, so data this extreme would be unlikely if the machine were on target — reject H₀. That says the effect is probably real; it says nothing about how big it is.`
          : `p = ${p} exceeds α = ${alpha}, so the data is not surprising under H₀ — do not reject. That is a failure to find evidence, not evidence that the machine is fine.`,
        hint: 'Compare p with α, then be careful about how strong a claim the result licenses.',
      });
    },
  }),

  template({
    id: 'error-types',
    conceptId: 'hypothesis-testing',
    description: 'Identify a Type I or Type II error in context.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const alpha = rng.pick([0.01, 0.05, 0.1]);
      const typeOne = rng.bool();

      return choiceExercise({
        prompt: typeOne
          ? `A test at α = ${alpha} rejects H₀, but H₀ was in fact true. What has happened, and how likely was it?`
          : `A test at α = ${alpha} fails to reject H₀, but H₀ was in fact false. What has happened?`,
        ...options(
          [
            typeOne
              ? { label: `A Type I error — a false alarm, with probability ${alpha}`, correct: true }
              : { label: 'A Type II error — a real effect was missed', correct: true },
            typeOne
              ? { label: `A Type II error, with probability ${alpha}`, misconceptionId: 'nonsignificant-taken-as-proof' }
              : { label: `A Type I error, with probability ${alpha}`, misconceptionId: 'p-value-as-probability-of-null' },
            typeOne
              ? { label: 'No error — rejecting is always the safe choice', misconceptionId: 'significance-taken-as-importance' }
              : { label: 'No error — failing to reject means H₀ was confirmed', misconceptionId: 'nonsignificant-taken-as-proof' },
            {
              label: `Lowering α would remove both kinds of error at once`,
              misconceptionId: 'significance-taken-as-importance',
            },
          ],
          rng.shuffle,
        ),
        summary: typeOne
          ? `Rejecting a true H₀ is a Type I error, and its probability is exactly the significance level: ${alpha}. That is what α *means*.`
          : `Failing to reject a false H₀ is a Type II error. Lowering α makes false alarms rarer and Type II errors more common — the two trade off and cannot both be minimised.`,
        hint: 'Type I rejects something true; Type II keeps something false. Which of those happened here?',
      });
    },
  }),

  /* ---------------------------------------------------------- correlation */
  template({
    id: 'correlation-from-z-products',
    conceptId: 'correlation',
    description: 'Compute r as the average product of z-scores.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const n = rng.pick([5, 6, 9, 11, 13, 17, 21]);
      const target = rng.pick([-0.92, -0.75, -0.4, 0.35, 0.55, 0.68, 0.85, 0.94]);
      const totalProducts = roundTo(target * (n - 1), 3);
      const answer = roundTo(totalProducts / (n - 1), 3);

      return numericExercise({
        prompt: `For ${n} pairs of data, the products of the standardised values total ${totalProducts}. What is the correlation coefficient r? Give your answer to three decimal places, keeping the sign.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `r is the *average* product of z-scores, over n − 1: ${totalProducts} ÷ ${n - 1} = ${answer}.`,
        hint: 'The formula averages the products. Over how many — n, or one fewer?',
        steps: [
          { label: 'The divisor.', latex: `n - 1 = ${n - 1}` },
          { label: 'Average the products.', latex: `\\frac{${totalProducts}}{${n - 1}} = ${answer}` },
        ],
        traps: distinct(answer, [
          // Divided by n rather than n − 1.
          trap(roundTo(totalProducts / n, 3), 'standard-error-divided-by-n'),
          // r² given where r was asked for.
          trap(roundTo(answer * answer, 3), 'r-and-r-squared-confused'),
          // The sign dropped.
          ...(answer < 0 ? [trap(roundTo(-answer, 3), 'correlation-sign-dropped')] : []),
        ]),
      });
    },
  }),

  template({
    id: 'coefficient-of-determination',
    conceptId: 'correlation',
    description: 'Convert between r and the proportion of variation explained.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const r = rng.pick([-0.9, -0.8, -0.6, -0.4, 0.3, 0.5, 0.7, 0.75, 0.95]);
      const answer = roundTo(r * r * 100, 2);

      return numericExercise({
        prompt: `A least-squares line through a scatter plot has r = ${r}. What percentage of the variation in y does the line account for? Give your answer to two decimal places.`,
        unit: '%',
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `The proportion of variation explained is r², not r: ${r}² = ${roundTo(r * r, 4)}, which is ${answer}%.`,
        hint: 'r measures how tightly the points hug the line. A different number measures how much of the variation the line accounts for.',
        steps: [
          { label: 'Square the correlation.', latex: `r^{2} = ${roundTo(r * r, 4)}` },
          { label: 'As a percentage.', latex: `${answer}\\%` },
        ],
        traps: distinct(answer, [
          // r quoted as a percentage — the classic confusion.
          trap(roundTo(Math.abs(r) * 100, 2), 'r-and-r-squared-confused'),
          // The unexplained proportion.
          trap(roundTo((1 - r * r) * 100, 2), 'complement-not-subtracted'),
        ]),
      });
    },
  }),

  template({
    id: 'correlation-limits',
    conceptId: 'correlation',
    description: 'Recognise where r is correct and misleading.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      /*
       * Every string here is already sentence-cased, and none is capitalised by
       * code. Upper-casing the first character mechanically turned
       * "r measures linear association only" into "R measures…", and r and R
       * are different symbols in statistics — the correlation and the
       * coefficient of multiple determination. A generated capital is not worth
       * a changed meaning, so the scenarios carry their own.
       */
      const scenario = rng.pick([
        {
          text: 'A scatter plot shows a perfect upward-then-downward arch, and yet r comes out at almost exactly 0',
          answer: 'The measurement is of *linear* association only, so a strong curved relationship can give r ≈ 0',
          wrong: 'correlation-assumed-for-any-relationship',
        },
        {
          text: 'Two studies of the same relationship report r = 0.8 and r = −0.8',
          answer: 'The relationships are equally strong and point in opposite directions',
          wrong: 'correlation-sign-dropped',
        },
        {
          text: 'A report quotes r = 0.6 and concludes that the line explains 60% of the variation',
          answer: 'The line explains r² = 36%, not 60%',
          wrong: 'r-and-r-squared-confused',
        },
        {
          text: 'Ice-cream sales and drownings are found to have r = 0.85',
          answer: 'A third variable, summer weather, can produce the association with no causal link',
          wrong: 'correlation-taken-as-cause',
        },
      ]);
      const filler = rng.pick([
        'The sample must have been too small to be meaningful',
        'The calculation must contain an arithmetic error',
        'The coefficient should have been computed on the ranks instead',
      ]);

      return choiceExercise({
        prompt: `${scenario.text}. What is the correct reading of this?`,
        ...options(
          [
            { label: scenario.answer, correct: true },
            { label: 'The relationship is weak and can be ignored', misconceptionId: scenario.wrong },
            { label: 'The two variables are unrelated', misconceptionId: scenario.wrong },
            { label: filler, misconceptionId: 'correlation-assumed-for-any-relationship' },
          ],
          rng.shuffle,
        ),
        summary: `${scenario.answer}. r is a precise measurement of one specific thing — linear association — and every misreading of it comes from asking it a question it does not answer.`,
        hint: 'What exactly does r measure, and what does it say nothing about?',
      });
    },
  }),

  /* ------------------------------------------------------ linear regression */
  template({
    id: 'regression-slope-from-r',
    conceptId: 'linear-regression',
    description: 'Rescale a correlation into a slope.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const r = rng.pick([-0.85, -0.6, 0.4, 0.55, 0.7, 0.8, 0.9]);
      const sx = rng.pick([1.5, 2, 2.5, 4, 5]);
      const sy = rng.pick([6, 8, 10, 12, 15, 20]);
      const answer = roundTo(r * (sy / sx), 3);

      return numericExercise({
        prompt: `Study hours x and test scores y have r = ${r}, s_x = ${sx} and s_y = ${sy}. What is the slope of the least-squares regression line of y on x? Give your answer to three decimal places, keeping the sign.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `The slope is the correlation rescaled into the units of the data: b = r × (s_y/s_x) = ${r} × (${sy}/${sx}) = ${answer} marks per hour.`,
        hint: 'r has no units and a slope must have them. What converts one into the other?',
        steps: [
          { label: 'The ratio of the spreads.', latex: `\\frac{s_y}{s_x} = \\frac{${sy}}{${sx}} = ${roundTo(sy / sx, 3)}` },
          { label: 'Multiply by the correlation.', latex: `${r} \\times ${roundTo(sy / sx, 3)} = ${answer}` },
        ],
        traps: distinct(answer, [
          // r reported as the slope, with no rescaling.
          trap(r, 'regression-slope-not-rescaled'),
          // The ratio inverted.
          trap(roundTo(r * (sx / sy), 3), 'regression-slope-not-rescaled'),
          // The spreads ratioed without the correlation.
          trap(roundTo(sy / sx, 3), 'regression-slope-not-rescaled'),
        ]),
      });
    },
  }),

  template({
    id: 'regression-intercept',
    conceptId: 'linear-regression',
    description: 'Force the line through the point of averages.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const slope = rng.pick([1.5, 2.5, 3.25, 4, 4.75, 6.5, -2.5, -3.5]);
      const meanX = rng.int(3, 20);
      const meanY = rng.int(30, 140);
      const answer = roundTo(meanY - slope * meanX, 3);

      return numericExercise({
        prompt: `A least-squares line has slope ${slope}, and the data has x̄ = ${meanX} and ȳ = ${meanY}. What is the intercept? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `Every least-squares line passes through the point of averages, so a = ȳ − bx̄ = ${meanY} − (${slope})(${meanX}) = ${answer}.`,
        hint: 'The line goes through (x̄, ȳ). Travelling back x̄ units from there along a slope of b lands you where?',
        steps: [
          { label: 'The line passes through the point of averages.', latex: `${meanY} = a + (${slope})(${meanX})` },
          { label: 'Solve for a.', latex: `a = ${meanY} - (${slope})(${meanX}) = ${answer}` },
        ],
        traps: distinct(answer, [
          // ȳ reported, with the bx̄ term never subtracted.
          trap(meanY, 'intercept-taken-as-mean'),
          // The sign of the subtraction reversed.
          trap(roundTo(meanY + slope * meanX, 3), 'intercept-taken-as-mean'),
          // The slope offered where the intercept was asked for.
          trap(slope, 'intercept-read-as-slope'),
        ]),
      });
    },
  }),

  template({
    id: 'least-squares-residual',
    conceptId: 'linear-regression',
    description: 'Compute a residual, in the right order.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const intercept = rng.int(20, 70);
      const slope = rng.pick([1.5, 2, 2.5, 3, 4, 4.5, 5]);
      const x = rng.int(2, 12);
      const predicted = intercept + slope * x;
      const gap = rng.pick([-9, -6.5, -4, -2.5, 2.5, 3, 5.5, 8]);
      const actual = roundTo(predicted + gap, 2);
      const answer = roundTo(actual - predicted, 2);

      return numericExercise({
        prompt: `A regression line is ŷ = ${intercept} + ${slope}x. A student who studied for ${x} hours actually scored ${actual}. What is the residual? Give your answer to two decimal places, keeping the sign.`,
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `Predicted: ${intercept} + ${slope}(${x}) = ${roundTo(predicted, 2)}. Residual = actual − predicted = ${actual} − ${roundTo(predicted, 2)} = ${answer}, so the point is ${answer > 0 ? 'above' : 'below'} the line.`,
        hint: 'Predict first. Then subtract in the order that makes a point above the line come out positive.',
        steps: [
          { label: 'Predict.', latex: `\\hat{y} = ${intercept} + ${slope}(${x}) = ${roundTo(predicted, 2)}` },
          { label: 'Actual minus predicted.', latex: `${actual} - ${roundTo(predicted, 2)} = ${answer}` },
        ],
        traps: distinct(answer, [
          // Predicted minus actual — every sign reversed.
          trap(roundTo(predicted - actual, 2), 'regression-residual-sign-inverted'),
          // The prediction quoted where the leftover was asked for.
          trap(roundTo(predicted, 2), 'intercept-read-as-slope'),
        ]),
      });
    },
  }),

  template({
    id: 'least-squares-extrapolation',
    conceptId: 'linear-regression',
    description: 'Judge a prediction outside the range of the data.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const intercept = rng.int(35, 60);
      const slope = rng.pick([3, 4, 4.25, 5, 6.5]);
      const maxX = rng.int(6, 10);
      const far = rng.int(20, 40);
      const prediction = roundTo(intercept + slope * far, 1);

      return choiceExercise({
        prompt: `A line ŷ = ${intercept} + ${slope}x was fitted to study hours between 1 and ${maxX}, with r = 0.88. Someone predicts a score of ${prediction} for ${far} hours of study. What is wrong with this?`,
        ...options(
          [
            {
              label: `${far} hours is far outside the range 1 to ${maxX}, where no data was collected — the line has no evidence there`,
              correct: true,
            },
            {
              label: `Nothing — r = 0.88 is strong, so the line can be trusted anywhere`,
              misconceptionId: 'extrapolated-beyond-the-data',
            },
            {
              label: `The slope should have been recomputed for the larger x`,
              misconceptionId: 'extrapolated-beyond-the-data',
            },
            {
              label: `r = 0.88 proves that studying causes higher scores, so the prediction is sound`,
              misconceptionId: 'correlation-taken-as-cause',
            },
          ],
          rng.shuffle,
        ),
        summary: `The line was fitted over 1 to ${maxX} hours, and outside that range nothing was measured. A high r says the line fits *inside* the data; it licenses nothing outside it — and a prediction of ${prediction} is what arithmetic gives when the evidence has run out.`,
        hint: 'Where does the evidence behind this line actually come from?',
      });
    },
  }),
];
